import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';

import { getThreadDetails, getPostsByThread, getCategoryByName, getUserDataById } from '../../utils/api';
import { IoAddCircleOutline } from "react-icons/io5";
import { useAuth } from '../../context/AuthContext';

import Tests from '../shorts/Tests';
import News from '../shorts/News';

import '../../assets/css/forum.css';

const Thread = () => {
    const { user } = useAuth(); // Auth-Context nutzen
    const { id, categoryName } = useParams();
    const [thread, setThread] = useState(null);
    const [category, setCategory] = useState('');
    const [posts, setPosts] = useState([]);
    const [usernames, setUsernames] = useState({}); // Speichert Benutzernamen für User-IDs
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState(null); // Benutzerrolle
    const [allowedRoles, setAllowedRoles] = useState([]); // Erlaubte Rollen

    useEffect(() => {
        const fetchThreadDetails = async () => {
            try {
                // Hole Thread-Daten
                const data = await getThreadDetails(id);
                if (data) {
                    setThread(data);
                    // Die Posts nun mit der neuen API-Methode holen
                    const postsData = await getPostsByThread(id);
                    // console.log(postsData);
                    setPosts(postsData.posts || []); // Posts aus der API setzen

                    // Kategorie holen
                    const categoryData = await getCategoryByName(categoryName);
                    setCategory(categoryData);

                    setUserRole(user && user.role_id ? user.role_id : null); // Setze null, wenn der Benutzer nicht angemeldet ist
                    setAllowedRoles(categoryData.allowed_role_ids ? categoryData.allowed_role_ids.split(',') : []);

                    // Benutzerinfos abrufen (Thread-Ersteller + alle Post-Autoren)
                    const userIds = [
                        data.user_id, // Ersteller des Threads
                        ...(Array.isArray(postsData.posts) ? postsData.posts.map(post => post.user_id) : []) // Nur, wenn posts existieren
                    ];

                    const uniqueUserIds = [...new Set(userIds)];
                    const userPromises = uniqueUserIds.map(async (userId) => {
                        const userData = await getUserDataById(userId);
                        return { userId, username: userData?.username || "Unbekannt" };
                    });

                    const usersArray = await Promise.all(userPromises);
                    const usersMap = usersArray.reduce((acc, item) => {
                        acc[item.userId] = item.username;
                        return acc;
                    }, {});

                    setUsernames(usersMap);
                } else {
                    setError("Thread nicht gefunden");
                }
            } catch (err) {
                console.error('Fehler beim Laden der Thread-Details:', err);
                setError('Fehler beim Laden des Threads.');
            } finally {
                setLoading(false);
            }
        };

        fetchThreadDetails();
    }, [id, categoryName]);

    const formatDate = (isoString) => {
        if (!isoString) return "—";
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} um ${hours}:${minutes} Uhr`;
    };

    // Funktion, um zu überprüfen, ob der Benutzer die Berechtigung hat
    const hasCreateThreadPermission = (userRole, allowedRoles) => {
        if (userRole === null || userRole === undefined) {
            return false;  // Benutzer ist nicht angemeldet oder hat keine Rolle
        }
        return allowedRoles.some(role => parseInt(role) === userRole);
    };

    const canCreateThread = hasCreateThreadPermission(userRole, allowedRoles);

    if (loading) {
        return <div>Lädt...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    if (!thread) {
        return <div>Thread nicht gefunden</div>;
    }

    return (
        <>
            <main>
                <div className='wrapper'>
                    <div className='rows'>
                        <div className='row-forum'>
                            <div className='category'>Community</div>
                            <div className='breadcrumb'>
                                <ul>
                                    <li><Link to={`/community`} viewTransition>Community</Link></li>
                                    <li>/</li>
                                    <li><Link to={`/community/${categoryName}`} viewTransition>{categoryName}</Link></li>
                                    <li>/</li>
                                    <li>{thread.title}</li>
                                </ul>
                            </div>
                            {canCreateThread && ( // Nur anzeigen, wenn der Benutzer berechtigt ist
                                <div className='create-thread-btn-container'>
                                    <Link to={`/community/create-post?categoryId=${id}&categoryName=${categoryName}&threadId=${thread.id}&threadName=${thread.title}`} className="create-thread-btn" viewTransition>
                                        <IoAddCircleOutline /> Beitrag erstellen
                                    </Link>
                                </div>
                            )}
                            <table className='forum'>
                                <thead>
                                    <tr>
                                        <th>Benutzer</th>
                                        <th>Beitrag</th>
                                    </tr>
                                </thead>
                                <tbody className='thread-details'>
                                    {/* Thread-Ersteller */}
                                    <tr>
                                        <td className="forum-profile">
                                            <div className="user-and-time">
                                                <div className="user">
                                                    <img src={thread.user_image} />
                                                    {(thread.role_name === "Abonnent" || thread.role_name === "Moderator" || thread.role_name === "Redakteur" || thread.role_name === "Admin") && (
                                                        <div className="user-badge">{thread.role_name}</div>
                                                    )}
                                                    <strong>{thread.username}</strong>
                                                </div>
                                                <div className="time">{formatDate(thread.created_at)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <h1>{thread.title}</h1>
                                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(thread.content) }} />
                                        </td>
                                    </tr>

                                    {/* Posts – nur anzeigen, wenn sie existieren */}
                                    {posts.length > 0 && posts.map((post) => (
                                        <tr key={post.id}>
                                            <td className="forum-profile">

                                                <div className="user-and-time">
                                                    <div className="user">
                                                        <img src={post.user_image} />
                                                        {(post.role_name === "Abonnent" || post.role_name === "Moderator" || post.role_name === "Redakteur" || post.role_name === "Admin") && (
                                                            <div className="user-badge">{post.role_name}</div>
                                                        )}
                                                        <strong>{post.username}</strong>
                                                    </div>
                                                    <div className="time">{formatDate(post.created_at)}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className='row'>
                            <Tests />
                            <News />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default Thread;
